import _ from 'lodash'
import { Flow, Screen } from '@/interfaces/FlowInterfaces'
import {
  buildDataSchemaForField,
  buildPayloadKey,
  prepareFlowJsonForMeta
} from '@/utils/flowMetaCompliance'
import { ensureJsonObject, getFormChildren, sanitizeFlowJsonForMeta } from '@/utils/flowJsonShape'

export { getFormChildren, sanitizeFlowJsonForMeta } from '@/utils/flowJsonShape'

export const WIZARD_SUPPORTED_COMPONENT_TYPES = new Set([
  'TextHeading',
  'TextSubheading',
  'TextBody',
  'TextCaption',
  'Image',
  'TextInput',
  'TextArea',
  'DatePicker',
  'RadioButtonsGroup',
  'CheckboxGroup',
  'Dropdown',
  'OptIn',
  'Footer'
])

const NON_FIELD_TYPES = new Set([
  'Image',
  'TextHeading',
  'TextSubheading',
  'TextBody',
  'TextCaption',
  'Footer'
])

export interface WizardCompatibilityResult {
  compatible: boolean
  reason?: string
}

export interface SerializeFromWizardOptions {
  mode: 'create' | 'edit'
}

function hasDynamicDataSource(flow: Record<string, unknown>): boolean {
  const screens = flow.screens as Screen[] | undefined

  if (!Array.isArray(screens)) {
    return false
  }

  return screens.some((screen) =>
    getFormChildren(screen).some(
      (child) =>
        typeof child?.['data-source'] === 'string' &&
        /^\$\{data\.[^}]+\}$/.test(child['data-source'])
    )
  )
}

export function isWizardCompatible(flow: Record<string, unknown> | null | undefined): WizardCompatibilityResult {
  if (!flow || typeof flow !== 'object') {
    return { compatible: false, reason: 'JSON del flow inválido.' }
  }

  if (flow.data_api_version != null && String(flow.data_api_version).trim() !== '') {
    return { compatible: false, reason: 'Flows con punto de conexión solo se editan con JSON.' }
  }

  if (hasDynamicDataSource(flow)) {
    return {
      compatible: false,
      reason: 'Flows con data-source dinámico (${data.*}) solo se editan con JSON.'
    }
  }

  const screens = flow.screens as Screen[] | undefined

  if (!Array.isArray(screens) || screens.length === 0) {
    return { compatible: false, reason: 'El flow debe incluir al menos una pantalla.' }
  }

  if (JSON.stringify(flow).match(/\$\{screen\.[^}]+\}/)) {
    return { compatible: false, reason: 'Flow con formato legacy; edítalo con JSON.' }
  }

  for (let screenIndex = 0; screenIndex < screens.length; screenIndex += 1) {
    const screen = screens[screenIndex]

    if (screen?.layout?.type !== 'SingleColumnLayout') {
      return { compatible: false, reason: `Pantalla ${screenIndex + 1}: layout debe ser SingleColumnLayout.` }
    }

    const layoutChildren = screen.layout.children

    if (!Array.isArray(layoutChildren) || layoutChildren.length !== 1) {
      return { compatible: false, reason: `Pantalla ${screenIndex + 1}: debe tener exactamente un Form en layout.children.` }
    }

    const form = layoutChildren[0]

    if (form?.type !== 'Form' || !Array.isArray(form.children)) {
      return { compatible: false, reason: `Pantalla ${screenIndex + 1}: estructura Form inválida.` }
    }

    const footerIndexes = form.children
      .map((child: any, index: number) => (child?.type === 'Footer' ? index : -1))
      .filter((index: number) => index >= 0)

    if (footerIndexes.length !== 1) {
      return { compatible: false, reason: `Pantalla ${screenIndex + 1}: debe tener un solo Footer.` }
    }

    if (footerIndexes[0] !== form.children.length - 1) {
      return { compatible: false, reason: `Pantalla ${screenIndex + 1}: el Footer debe ser el último componente.` }
    }

    for (const child of form.children) {
      if (!child?.type || !WIZARD_SUPPORTED_COMPONENT_TYPES.has(child.type)) {
        return {
          compatible: false,
          reason: `Pantalla ${screenIndex + 1}: componente "${child?.type || 'desconocido'}" no soportado por el asistente.`
        }
      }
    }
  }

  return { compatible: true }
}

export function normalizeForWizard(flow: Record<string, unknown> | Flow): Flow {
  const cloned = _.cloneDeep(flow) as Flow
  const screens = cloned.screens || []

  screens.forEach((screen, screenIndex) => {
    const children = getFormChildren(screen)

    children.forEach((child) => {
      if (child.type === 'Image') {
        delete child.srcForPreview
      }
    })

    const footer = children.find((child) => child.type === 'Footer')

    if (footer) {
      const isLastScreen = screenIndex === screens.length - 1
      const actionName = footer['on-click-action']?.name

      if (actionName !== 'navigate' && actionName !== 'complete') {
        footer['on-click-action'] = {
          name: isLastScreen ? 'complete' : 'navigate',
          payload: ensureJsonObject(footer['on-click-action']?.payload)
        }
      }

      if (!isLastScreen && !footer['on-click-action']?.next) {
        footer['on-click-action'].next = {
          type: 'screen',
          name: screens[screenIndex + 1]?.id
        }
      }
    }

    screen.data = ensureJsonObject(screen.data) as Screen['data']
    screen.terminal = screenIndex === screens.length - 1
    delete screen.requestsData
    delete screen.success
  })

  cloned.version = '7.0'

  return cloned
}

function assignScreenIds(screens: Screen[]): void {
  screens.forEach((screen, index) => {
    screen.id = `SCREEN_${String.fromCharCode(65 + index)}`
  })
}

function getFieldComponents(screen: Screen): any[] {
  return getFormChildren(screen).filter((child) => !NON_FIELD_TYPES.has(child.type))
}

function ensureFieldNames(screens: Screen[]): void {
  screens.forEach((screen, screenIndex) => {
    let fieldIndex = 0

    getFieldComponents(screen).forEach((child) => {
      if (child.name && typeof child.name === 'string') {
        fieldIndex += 1
        return
      }

      const formattedLabel = _.snakeCase(_.deburr(child.label || child.type || 'field'))

      child.name = `${child.type}_${formattedLabel}_${fieldIndex}`
      fieldIndex += 1

      if (
        child.type === 'RadioButtonsGroup' ||
        child.type === 'CheckboxGroup' ||
        child.type === 'Dropdown'
      ) {
        child['data-source']?.forEach((element: any, optionIndex: number) => {
          const formattedTitle = _.snakeCase(_.deburr(element.title || `option_${optionIndex}`))
          element.id = `${optionIndex}_${formattedTitle}`
        })
      }
    })
  })
}

function buildMetaScreenConnections(screens: Screen[]): void {
  const inheritedDataByScreen = new Map<string, Record<string, unknown>>()

  screens.forEach((screen, screenIndex) => {
    const inherited = { ...ensureJsonObject(inheritedDataByScreen.get(screen.id)) }
    screen.data = inherited as Screen['data']

    const footer = getFormChildren(screen).find((child) => child.type === 'Footer')

    if (!footer) {
      return
    }

    const isLastScreen = screenIndex === screens.length - 1
    screen.terminal = isLastScreen

    if (!isLastScreen) {
      const navigatePayload: Record<string, unknown> = {}
      const nextScreen = screens[screenIndex + 1]
      const nextDataSchemas: Record<string, unknown> = { ...ensureJsonObject(nextScreen.data) }

      getFieldComponents(screen).forEach((child, fieldIndex) => {
        const payloadKey = buildPayloadKey(screenIndex, fieldIndex, child.name)
        navigatePayload[payloadKey] = `\${form.${child.name}}`
        nextDataSchemas[payloadKey] = buildDataSchemaForField(child.type)
      })

      footer['on-click-action'] = {
        name: 'navigate',
        payload: navigatePayload,
        next: {
          type: 'screen',
          name: nextScreen.id
        }
      }

      inheritedDataByScreen.set(nextScreen.id, nextDataSchemas)
      return
    }

    const completePayload: Record<string, unknown> = {}

    screens.forEach((sourceScreen, sourceScreenIndex) => {
      getFieldComponents(sourceScreen).forEach((child, fieldIndex) => {
        const payloadKey = buildPayloadKey(sourceScreenIndex, fieldIndex, child.name)

        if (sourceScreenIndex === screenIndex) {
          completePayload[payloadKey] = `\${form.${child.name}}`
          return
        }

        completePayload[payloadKey] = `\${data.${payloadKey}}`
      })
    })

    footer['on-click-action'] = {
      name: 'complete',
      payload: completePayload
    }

    delete footer['on-click-action'].next
  })

}

export function serializeFromWizardForSave(
  flow: Flow,
  options: SerializeFromWizardOptions
): { flow: Record<string, unknown>; validationErrors: string[] } {
  const cloned = _.cloneDeep(flow) as Flow
  const screens = cloned.screens || []

  screens.forEach((screen) => {
    delete screen.requestsData
    delete screen.success
  })

  if (options.mode === 'create') {
    assignScreenIds(screens)
  }

  ensureFieldNames(screens)
  buildMetaScreenConnections(screens)

  const prepared = prepareFlowJsonForMeta({
    version: '7.0',
    screens
  } as Record<string, unknown>)

  return {
    flow: prepared.flow,
    validationErrors: prepared.validation.errors
  }
}
